import { useState } from "react";
import { useGlobalReducer } from "../../../GlobalContext";
import GlobalDispatchMethods from "../../context/globalDispatchMethods";

type ServiceData = Record<string, unknown> & { ErrorMessage?: string };
type ServiceCall = (
  dispatch: null,
  emailAddress: string
) => Promise<{ data?: ServiceData } | undefined>;

export interface SuppressionResult {
  success: boolean;
  message: string;
}

/** Checks an address against one suppression list, and takes it off when it's on. */
export function useSuppressionCheck({
  check,
  remove,
  isListed,
  checkFailedMessage,
  removeFailedMessage,
  listedMessage,
  notListedMessage,
  removedMessage,
  notRemovedMessage,
}: {
  check: ServiceCall;
  remove: ServiceCall;
  isListed: (data: ServiceData) => boolean;
  checkFailedMessage: string;
  removeFailedMessage: string;
  listedMessage: (emailAddress: string) => string;
  notListedMessage: (emailAddress: string) => string;
  removedMessage: (emailAddress: string) => string;
  notRemovedMessage: (emailAddress: string) => string;
}) {
  const globalDispatch = useGlobalReducer();
  const [emailAddress, setEmailAddress] = useState("");
  const [checkedAddress, setCheckedAddress] = useState("");
  const [checkData, setCheckData] = useState<ServiceData | null>(null);
  const [listed, setListed] = useState(false);
  const [checkResult, setCheckResult] = useState<SuppressionResult | null>(null);
  const [removeResult, setRemoveResult] = useState<SuppressionResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const notifyFailure = (message: unknown) =>
    globalDispatch({
      type: GlobalDispatchMethods.AddNotification,
      notification: { message, success: false },
      dispatch: globalDispatch,
    });

  // The services are passed no dispatch, so failures are reported here once.
  const call = async (service: ServiceCall, address: string, failedMessage: string) => {
    try {
      const response = await service(null, address);
      if (!response?.data) {
        notifyFailure(failedMessage);
        return null;
      }
      if (response.data.ErrorMessage) {
        notifyFailure(response.data.ErrorMessage);
        return null;
      }
      return response.data;
    } catch (error) {
      console.error(error);
      notifyFailure(error);
      return null;
    }
  };

  const runCheck = async () => {
    if (!emailAddress) {
      return;
    }

    setIsSubmitting(true);
    setCheckResult(null);
    setRemoveResult(null);
    const data = await call(check, emailAddress, checkFailedMessage);
    const isOnList = Boolean(data && isListed(data));
    setCheckData(data);
    setListed(isOnList);
    setCheckedAddress(emailAddress);
    setCheckResult(
      data && {
        success: !isOnList,
        message: isOnList
          ? listedMessage(emailAddress)
          : notListedMessage(emailAddress),
      }
    );
    setIsSubmitting(false);
  };

  const runRemove = async () => {
    if (!checkedAddress) {
      return;
    }

    setIsSubmitting(true);
    const data = await call(remove, checkedAddress, removeFailedMessage);
    const removed = Boolean(data?.Removed);
    setCheckResult(null);
    setListed(false);
    setRemoveResult(
      data && {
        success: removed,
        message: removed
          ? removedMessage(checkedAddress)
          : notRemovedMessage(checkedAddress),
      }
    );
    setIsSubmitting(false);
  };

  return {
    emailAddress,
    setEmailAddress,
    checkData,
    listed,
    checkResult,
    removeResult,
    isSubmitting,
    runCheck,
    runRemove,
  };
}
